/**
 * Earnings — single source of truth for the "Daily earnings / takings"
 * feature. Used by:
 *   - Shop-admin Dashboard card
 *   - Barber Dashboard card (scoped to their barber_id)
 *   - Analytics page line-chart
 *   - Master-admin ShopDetail line-chart
 *   - Data export ZIP (daily_earnings.csv)
 *
 * Rules (from spec, 7 Aug 2026):
 *   - Only bookings with `paid_in_full = true` count.
 *   - Amount taken per booking = deposit_amount + balance_paid_amount
 *     (actual money that changed hands — resilient to price edits).
 *   - Cancelled and no_show bookings are excluded.
 *   - Deposits, on their own, never add to or subtract from earnings.
 *
 * Split:
 *   - CASH  = balance_paid_amount where balance_paid_method = 'cash'
 *   - CARD  = deposit_amount (Stripe prepay) + balance_paid_amount where method = 'card'
 *
 * These two totals ALWAYS sum to the headline "earnings" number.
 */
import { supabase } from './supabase';

// ---- Period definitions ---------------------------------------------------
// Keys map to the dropdown values used across every surface. Length in days
// is used only for the previous-period comparison and axis density; the
// actual window is computed from calendar boundaries (start of week/month)
// so labels stay intuitive.
export const EARNINGS_PERIODS = [
  { key: 'week',    label: 'This week' },
  { key: 'month',   label: 'This month' },
  { key: '3m',      label: '3 months' },
  { key: '6m',      label: '6 months' },
  { key: '9m',      label: '9 months' },
  { key: 'year',    label: 'Year' },
];

// Standard plan is capped to short windows; Premium unlocks the full list.
export const periodsForPlan = (plan) => {
  if (plan === 'premium') return EARNINGS_PERIODS;
  return EARNINGS_PERIODS.filter(p => p.key === 'week' || p.key === 'month');
};

// ---- Range resolution -----------------------------------------------------
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const startOfWeekMon = (d) => {
  const x = startOfDay(d);
  const diff = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - diff);
  return x;
};
const addDays  = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const dateKey   = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

export const rangeForPeriod = (key, now = new Date()) => {
  let from;
  const to = endOfDay(now);
  switch (key) {
    case 'week':  from = startOfWeekMon(now); break;
    case 'month': from = new Date(now.getFullYear(), now.getMonth(), 1); break;
    case '3m':    from = startOfDay(addMonths(now, -3)); break;
    case '6m':    from = startOfDay(addMonths(now, -6)); break;
    case '9m':    from = startOfDay(addMonths(now, -9)); break;
    case 'year':  from = startOfDay(addMonths(now, -12)); break;
    default:      from = startOfWeekMon(now);
  }
  return { from, to };
};

// Bucket granularity — daily for week/month, weekly for 3m/6m/9m/year.
// Keeps the line chart readable regardless of range length.
export const bucketGranularity = (key) => (
  (key === 'week' || key === 'month') ? 'day' : 'week'
);

// ---- Core fetch + rollup --------------------------------------------------
/**
 * Fetch paid_in_full bookings + roll them up into buckets.
 *
 * @param {Object}  args
 * @param {string}  args.shopId
 * @param {?string} args.barberId — optional; when supplied earnings are
 *                                  scoped to that single barber. When null
 *                                  the caller sees shop-wide.
 * @param {string}  args.period    — one of EARNINGS_PERIODS keys.
 * @returns {Promise<{
 *   total:  number,
 *   cash:   number,
 *   card:   number,
 *   count:  number,
 *   series: Array<{ bucket: string, label: string, total: number, cash: number, card: number, count: number }>,
 *   rawByDay: Array<{ date: string, barber_id: string, total: number, cash: number, card: number, count: number }>,
 * }>}
 */
export const fetchEarnings = async ({ shopId, barberId = null, period = 'week' }) => {
  const { from, to } = rangeForPeriod(period);
  let q = supabase.from('bookings')
    .select('starts_at,barber_id,deposit_amount,balance_paid_amount,balance_paid_method,paid_in_full,status')
    .eq('shop_id', shopId)
    .eq('paid_in_full', true)
    .neq('status', 'cancelled')
    .neq('status', 'no_show')
    .gte('starts_at', from.toISOString())
    .lte('starts_at', to.toISOString());
  if (barberId) q = q.eq('barber_id', barberId);
  const { data, error } = await q;
  if (error) throw error;
  const rows = data || [];

  // Per-booking amount + method attribution (see file header for rules).
  const attribute = (b) => {
    const dep  = Number(b.deposit_amount || 0);
    const bal  = Number(b.balance_paid_amount || 0);
    const isCashBal = b.balance_paid_method === 'cash';
    const cash = isCashBal ? bal : 0;
    // Everything not cash-at-counter counts as card (Stripe deposit + any
    // balance taken via the terminal or the card option in PaymentActionSheet).
    const card = (dep) + (isCashBal ? 0 : bal);
    return { amount: dep + bal, cash, card };
  };

  // --- Aggregate raw per-day per-barber (used by data export) --------------
  const rawMap = new Map(); // key = `${date}::${barber_id}`
  const totals = { total: 0, cash: 0, card: 0, count: 0 };
  rows.forEach(b => {
    const d = dateKey(new Date(b.starts_at));
    const key = `${d}::${b.barber_id || ''}`;
    const cur = rawMap.get(key) || { date: d, barber_id: b.barber_id, total: 0, cash: 0, card: 0, count: 0 };
    const { amount, cash, card } = attribute(b);
    cur.total += amount;
    cur.cash  += cash;
    cur.card  += card;
    cur.count += 1;
    rawMap.set(key, cur);
    totals.total += amount;
    totals.cash  += cash;
    totals.card  += card;
    totals.count += 1;
  });

  // --- Bucketed series for the chart --------------------------------------
  const gran = bucketGranularity(period);
  const bucketKey = (d) => {
    if (gran === 'day') return dateKey(d);
    // ISO-week bucket: use the Monday of that week as the key.
    return dateKey(startOfWeekMon(d));
  };
  const seriesMap = new Map();
  rows.forEach(b => {
    const dt = new Date(b.starts_at);
    const bk = bucketKey(dt);
    const cur = seriesMap.get(bk) || { bucket: bk, total: 0, cash: 0, card: 0, count: 0 };
    const { amount, cash, card } = attribute(b);
    cur.total += amount;
    cur.cash  += cash;
    cur.card  += card;
    cur.count += 1;
    seriesMap.set(bk, cur);
  });

  // Fill every bucket in the range so the line doesn't have visual gaps.
  const buckets = [];
  const cursor = new Date(from);
  while (cursor <= to) {
    const bk = bucketKey(cursor);
    if (!buckets.find(b => b.bucket === bk)) {
      const row = seriesMap.get(bk) || { bucket: bk, total: 0, cash: 0, card: 0, count: 0 };
      buckets.push({ ...row, label: shortLabel(bk, gran) });
    }
    cursor.setDate(cursor.getDate() + (gran === 'day' ? 1 : 7));
  }

  return {
    ...totals,
    series: buckets,
    rawByDay: Array.from(rawMap.values()).sort((a, b) => a.date.localeCompare(b.date)),
  };
};

// Human-readable label for a bucket key. Kept short so line-chart X-axis
// stays legible even for the year view.
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const shortLabel = (bucketKey, gran) => {
  const [y, m, d] = bucketKey.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (gran === 'day') return `${d} ${MONTH_SHORT[m - 1]}`;
  return `${d} ${MONTH_SHORT[m - 1]}`;
};

/**
 * Money formatter for headline + split values.
 *   - Integer amounts render clean (£60)
 *   - Non-integers keep pennies (£60.50) so Cash + Card always sums to Total
 * Never abbreviates — abbreviation is unsafe for financial reconciliation.
 * Use fmtMoneyShort() only for chart axis labels.
 */
export const fmtMoney = (n) => {
  const v = Number(n || 0);
  const hasPennies = Math.round(v * 100) % 100 !== 0;
  return `£${v.toFixed(hasPennies ? 2 : 0)}`;
};

/** Compact form for chart axis labels only. Never for headline values. */
export const fmtMoneyShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `£${(v / 1000).toFixed(1)}k`;
  return `£${v.toFixed(0)}`;
};
